// components/common/ProductCard.tsx
import { useCart } from '@/hooks/useCart';
import { useWishlist } from '@/hooks/useWishlist';
import { Product, formatPrice, calculateDiscountPercentage, isBook, formatAuthorsDisplay } from '@bookstore/shared';
import { Heart, ShoppingCart, Star, Plus, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Placeholder from '@/assets/placeholder.webp';

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const { isInWishlist, toggleWishlist, isPending: wishlistPending } = useWishlist();
  const { addToCart, isInCart, getItemQuantity, isPending: cartPending } = useCart();
  const [imageLoaded, setImageLoaded] = useState(false);

  const inWishlist = isInWishlist(product.id);
  const inCart = isInCart(product.id);
  const cartQuantity = getItemQuantity(product.id);

  const discount = product.discountedPrice
    ? calculateDiscountPercentage(product.price, product.discountedPrice)
    : 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();

    if (product.stock === 0) {
      return;
    }

    addToCart(product.id, 1);
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    toggleWishlist(product.id);
  };

  const productUrl = `/product/${product.seoUrl}`;

  // Get stock status
  const stockStatus = 
    product.stock === 0 ? 'out' : 
    product.stock <= 5 ? 'low' : 
    'in';

  return (
    <Link
      to={productUrl}
      itemProp="url"
      className="group relative bg-white rounded-lg  hover:border-primary-200 hover:shadow-xl transition-all duration-300 overflow-hidden"
    >
      {/* Image Container */}
      <div className="relative aspect-[6/4] overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
        {/* Shimmer loading effect */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-primary-100 via-primary-50 to-primary-100 bg-[length:200%_100%] animate-shimmer" />
        )}

        {product.images && product.images.length > 0 ? (
          <>
            {/* Blurred background image */}
            <img
              src={product.images[0]}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-40"
              loading="lazy"
            />
            {/* Main image - object-contain */}
            <img
              src={product.images[0]}
              alt={product.title.slice(0, 50) + (product.title.length > 50 ? '...' : '')}
              onLoad={() => setImageLoaded(true)}
              className={`relative z-0 w-full h-full object-contain p-0 transition-all duration-500 ${
                imageLoaded 
                  ? 'opacity-100 group-hover:scale-105' 
                  : 'opacity-0'
              }`}
              loading="lazy"
            />
          </>
        ) : (
          <>
            {/* Blurred background placeholder */}
            <img
              src={Placeholder}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40"
              loading="lazy"
            />
            {/* Main placeholder - object-contain */}
            <img
              src={Placeholder}
              alt="Placeholder"
              onLoad={() => setImageLoaded(true)}
              className={`relative w-full h-full object-contain transition-all duration-500 ${
                imageLoaded 
                  ? 'opacity-100 group-hover:scale-105' 
                  : 'opacity-0'
              }`}
              loading="lazy"
            />
          </>
        )}

        {/* Gradient Overlay on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Top Badges Row */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          {/* Discount Badge */}
          {discount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md shadow-lg animate-in fade-in slide-in-from-left-2 duration-300">
              <Sparkles className="w-3 h-3" />
              <span>{discount}% OFF</span>
            </div>
          )}

          {/* Wishlist Button */}
          <button
            onClick={handleToggleWishlist}
            disabled={wishlistPending}
            className={`
              p-2.5 rounded-md shadow-lg backdrop-blur-sm transition-all duration-300
              ${inWishlist 
                ? 'bg-primary-50 hover:bg-primary-100' 
                : 'bg-white/90 hover:bg-white'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
              transform hover:scale-110 active:scale-95
            `}
            title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              className={`w-4 h-4 transition-all duration-300 ${
                inWishlist 
                  ? 'fill-primary text-primary scale-110' 
                  : 'text-gray-600 hover:text-primary'
              }`}
            />
          </button>
        </div>

        {/* Stock Status Badge */}
        {stockStatus === 'low' && (
          <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-semibold rounded-md shadow-lg">
            Only {product.stock} left!
          </div>
        )}

        {/* Out of Stock Overlay */}
        {stockStatus === 'out' && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-900/60 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block px-4 py-2 bg-white rounded-xl shadow-xl">
                <span className="text-gray-900 font-bold text-sm">Out of Stock</span>
              </div>
              {/* <p className="text-white text-xs mt-2">Notify when available</p> */}
            </div>
          </div>
        )}

        {/* Cart Quantity Badge */}
        {inCart && cartQuantity > 0 && (
          <div className="absolute bottom-3 right-3 min-w-[32px] h-8 px-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-md flex items-center justify-center font-bold text-sm shadow-lg animate-in zoom-in duration-200">
            {cartQuantity}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-semibold text-primary line-clamp-2 mb-1.5 group-hover:text-primary-800 transition-colors duration-200 leading-snug overflow-ellipsis overflow-hidden whitespace-nowrap">
          {product.title.slice(0, 50) + (product.title.length > 50 ? '...' : '')}
        </h3>
        
        {/* Author/Brand */}
        <p className="text-sm text-gray-500 mb-2.5 truncate h-5" title={isBook(product) ? product.author : (product as any).brand || 'Stationery'}>
          {isBook(product) ? formatAuthorsDisplay(product.author, 2) : (product as any).brand || 'Stationery'}
        </p>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.floor(product.rating)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'fill-gray-200 text-gray-200'
                }`}
              />
            ))}
          </div>
          <span className="text-sm font-semibold text-gray-700">
            {product.rating.toFixed(1)}
          </span>
          <span className="text-xs text-gray-400">
            ({product.reviewCount})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-xl font-bold text-primary-900 tracking-tight">
            {formatPrice(product.discountedPrice || product.price)}
          </span>
          {product.discountedPrice && (
            <span className="text-sm text-gray-400 line-through">
              {formatPrice(product.price)}
            </span>
          )}
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={product.stock === 0 || cartPending}
          className={`
            w-full py-2.5 px-4 rounded-md font-semibold transition-all duration-200
            flex items-center justify-center gap-2
            transform active:scale-95
            ${
              product.stock === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : inCart
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/25'
                : 'bg-gradient-to-r from-primary to-primary-500 text-white hover:from-primary-700 hover:to-primary-800 shadow-lg shadow-primary-500/25'
            }
          `}
        >
          {product.stock === 0 ? (
            <>
              <ShoppingCart className="w-4 h-4" />
              <span>Out of Stock</span>
            </>
          ) : inCart ? (
            <>
              <Plus className="w-4 h-4" />
              <span>Add More</span>
            </>
          ) : (
            <>
              <ShoppingCart className="w-4 h-4" />
              <span>Add to Cart</span>
            </>
          )}
        </button>
      </div>

      {/* Hover Border Effect */}
      <div className="absolute inset-0 rounded-lg border border-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </Link>
  );
};

export default ProductCard;